import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { Periodical, PeriodicalFormData, ColumnFilterOption } from "@/types";

export default function PeriodicalRegisterPage() {
  const { api, user } = useAuth();
  const [items, setItems] = useState<Periodical[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Periodical | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [form, setForm] = useState<PeriodicalFormData>({
    title: "",
    type: "journal",
    issn: "",
    frequency: "",
    volume: "",
    issue_number: "",
    date: "",
    status: "active",
  });
  const isAdmin = user?.role === "admin" || user?.role === "librarian";

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      const textParts = [columnFilters.title, columnFilters.issn].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.type) params.type = columnFilters.type;
      if (columnFilters.status) params.status = columnFilters.status;
      const res = await api.get("/periodicals", { params });
      setItems(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", type: "journal", issn: "", frequency: "", volume: "", issue_number: "", date: "", status: "active" });
    setDialogOpen(true);
  };
  const openEdit = (item: Periodical) => {
    setEditing(item);
    setForm({
      title: item.title,
      type: item.type,
      issn: item.issn || "",
      frequency: item.frequency || "",
      volume: item.volume || "",
      issue_number: item.issue_number || "",
      date: item.date || "",
      status: item.status,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    try {
      if (editing) {
        await api.put(`/periodicals/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/periodicals", form);
        toast.success("Created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete?")) return;
    try {
      await api.delete(`/periodicals/${id}`);
      toast.success("Deleted");
      fetchItems();
    } catch {
      toast.error("Failed");
    }
  };

  const typeOptions: ColumnFilterOption[] = [
    { label: "Journal", value: "journal" },
    { label: "Magazine", value: "magazine" },
    { label: "Newspaper", value: "newspaper" },
  ];
  const statusOptions: ColumnFilterOption[] = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const columns = [
    {
      accessorKey: "title",
      header: "Title",
      meta: { filterVariant: "text", filterPlaceholder: "Title..." },
      cell: ({ row }) => <span className="font-medium">{row.original.title}</span>,
    },
    {
      accessorKey: "type",
      header: "Type",
      meta: { filterVariant: "select", filterOptions: typeOptions, filterPlaceholder: "Type" },
      cell: ({ row }) => (
        <Badge variant="outline" className="capitalize">
          {row.original.type}
        </Badge>
      ),
    },
    {
      accessorKey: "issn",
      header: "ISSN",
      meta: { filterVariant: "text", filterPlaceholder: "ISSN..." },
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.issn}</span>,
    },
    { accessorKey: "volume", header: "Volume" },
    { accessorKey: "issue_number", header: "Issue" },
    { accessorKey: "frequency", header: "Frequency" },
    {
      accessorKey: "status",
      header: "Status",
      meta: { filterVariant: "select", filterOptions: statusOptions, filterPlaceholder: "Status" },
      cell: ({ row }) => (
        <Badge className={row.original.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-gray-100 text-gray-800"}>
          {row.original.status}
        </Badge>
      ),
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(row.original.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="periodical-register-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Periodical Register
          </h1>
        </div>
        {isAdmin && (
          <Button className="bg-primary hover:bg-primary/90" onClick={openNew} data-testid="add-periodical-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Periodical
          </Button>
        )}
      </div>
      <DataTable
        columns={columns}
        data={items}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        data-testid="periodicals-table"
      />
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Add"} Periodical</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="periodical-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="journal">Journal</SelectItem>
                  <SelectItem value="magazine">Magazine</SelectItem>
                  <SelectItem value="newspaper">Newspaper</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>ISSN</Label>
              <Input value={form.issn} onChange={(e) => setForm({ ...form, issn: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>Frequency</Label>
              <Input value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} placeholder="Monthly, Quarterly..." />
            </div>
            <div className="space-y-1.5">
              <Label>Volume</Label>
              <Input value={form.volume} onChange={(e) => setForm({ ...form, volume: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Issue Number</Label>
              <Input value={form.issue_number} onChange={(e) => setForm({ ...form, issue_number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={handleSave} data-testid="save-periodical-btn">
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
